import { Container } from "pixi.js";
import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity } from "../../../core/entity/Entity";
import { GameSprite } from "../../../core/entity/GameSprite";
import { on } from "../../../core/entity/handler";
import { groupPieces } from "../../../game/bench-work/bench-group";
import { BenchPlacement } from "../../../game/bench-work/bench-layout";
import { ShellStore } from "../../ShellStore";
import { BenchArrangeView } from "./BenchArrangeView";
import { BenchDive } from "./BenchDive";
import { benchStage } from "./benchStage";
import { PieceMotion } from "./PieceMotion";

/** Where a piece about to be drawn is coming from. */
interface PieceArrival {
  readonly from: BenchPlacement;
  readonly lifted: boolean;
}

/**
 * The pieces lying on the run's tops — one entity per piece, each
 * mounted in this view's container ("parent" placement), which itself
 * rides the dive's frame. Pieces come and go with the sim's
 * arrangement; a standing piece keeps its entity across redraws, so a
 * turn or a tumble is motion on a standing sprite instead of a swap
 * (the old scene's spring and FlipTumble).
 *
 * The piece riding the hand is not among these — the arranging view
 * draws it where the hand has it, and hands its holder over on release
 * (`takeHandoff`), so a drop that snaps into a seat springs there on
 * the same holder instead of reappearing already seated.
 *
 * A piece that is new to the tops arrives the same way when the view
 * that committed it says where it came from (`expectArrival`): a board
 * pried out of the pallet starts on its berth and slides to the pile.
 * Anything that puts something new on the bench — a piece coming off a
 * tray, later — can register its origin the same way.
 */
export class BenchPiecesView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private root: Container & GameSprite;
  private views = new Map<string, BenchPieceView>();
  private drawnKey: string | null = null;
  /** Where pieces about to appear are coming from, by id — spent by the
   * sync that first draws them, forgotten by the one that doesn't. */
  private arrivals = new Map<string, PieceArrival>();

  /**
   * @param rimLayer where a lit piece's rim is drawn — the dive view's
   * layer over everything on the stage, threaded through to each
   * piece's PieceMotion.
   */
  constructor(private readonly rimLayer: Container) {
    super();
    this.root = new Container() as Container & GameSprite;
    this.root.layerName = "parent";
    this.sprite = this.root;
  }

  /**
   * Announce where a piece the sim is about to show came from, before
   * the sync that first draws it: its holder is seeded there (lifted,
   * if it came up off the surface) and the springs carry it into its
   * committed seat. Reduced motion still snaps (see PieceMotion).
   */
  expectArrival(
    pieceId: string,
    from: BenchPlacement,
    opts: { lifted?: boolean } = {},
  ): void {
    this.arrivals.set(pieceId, { from, lifted: opts.lifted ?? false });
  }

  @on("render")
  onRender() {
    const game = this.game;
    const dive = game.entities.tryGetSingleton(BenchDive);
    const stage = benchStage(game);
    if (!dive || !stage) {
      this.clear();
      return;
    }

    // Sync when the sim moved or a different bench opened; the fit also
    // follows the window (cheap to recompute, compared each frame). The
    // springs themselves run on every piece's own render, gated or not.
    const store = game.entities.tryGetSingleton(ShellStore);
    const { fit, group } = stage;
    const fitKey = `${fit.pxPerIn},${fit.originX},${fit.originY}`;
    const key = `${dive.openBenchKey}|${store?.version ?? 0}|${fitKey}`;
    if (key === this.drawnKey) return;
    this.drawnKey = key;

    // Everything lying on the tops, where the layout says it lies —
    // except a piece riding the hand, which the arranging view draws
    // where the hand has it rather than where it was set down. A piece a
    // running job claimed left the pile with it, and that job's gesture
    // surface draws it.
    const arrange = game.entities.tryGetSingleton(BenchArrangeView);
    const dragging = arrange?.draggingId();
    const lying = groupPieces(group).filter(
      (piece) => piece.material.id !== dragging,
    );

    // Sync the entities: pieces come and go, standing ones retarget —
    // the springs carry a changed angle or flip as motion.
    const seen = new Set<string>();
    for (const piece of lying) {
      seen.add(piece.material.id);
      let view = this.views.get(piece.material.id);
      if (!view) {
        // A just-released piece keeps the holder that rode the hand, so
        // the springs carry it from the drop point into its seat instead
        // of it reappearing already there — settling back down out of
        // the carry's lift on the way. A piece announced as arriving
        // (expectArrival) starts its fresh holder where it came from.
        const motion =
          arrange?.takeHandoff(piece.material.id) ??
          this.arrivingMotion(piece.material.id);
        motion.setCarried(false);
        // Back on the bench, a piece is its full size — a tile lifted off
        // a tray grows the rest of the way as it settles.
        motion.setTileScale(1);
        view = this.addChild(new BenchPieceView(piece.material.id, motion));
        this.views.set(piece.material.id, view);
      }
      view.motion.retarget(piece.material, piece.placement, fit);
    }
    // An arrival the sync didn't draw was for a piece that never showed.
    this.arrivals.clear();
    for (const [id, view] of this.views) {
      if (seen.has(id)) continue;
      view.destroy();
      this.views.delete(id);
    }
    // Standing holders keep their mount order otherwise, and the stack's
    // draw order has to match the layout's (pieceUnder reads it in
    // reverse) — so re-stack to the group's order every sync.
    lying.forEach((piece, index) => {
      const view = this.views.get(piece.material.id);
      if (view) this.root.setChildIndex(view.motion.holder, index);
    });
  }

  /** A holder for a piece drawn for the first time, seeded at its
   * announced origin when it has one. */
  private arrivingMotion(pieceId: string): PieceMotion {
    const motion = new PieceMotion(this.rimLayer);
    const arrival = this.arrivals.get(pieceId);
    if (arrival) motion.arriveFrom(arrival.from, { lifted: arrival.lifted });
    return motion;
  }

  private clear(): void {
    this.drawnKey = null;
    this.arrivals.clear();
    if (this.views.size === 0) return;
    for (const view of this.views.values()) view.destroy();
    this.views.clear();
  }
}

/**
 * One piece lying on the bench: a thin entity around its PieceMotion —
 * the holder is the sprite, mounted in the pieces container by
 * placement, and the springs and the hand's light run on this entity's
 * own render. The rim ghost stays PieceMotion's manual business: it
 * mirrors the holder from the dive's rim layer, a different container
 * than the one this entity mounts in, and is only up while the piece
 * is lit.
 */
export class BenchPieceView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  constructor(
    readonly pieceId: string,
    readonly motion: PieceMotion,
  ) {
    super();
    const holder = motion.holder as Container & GameSprite;
    holder.layerName = "parent";
    this.sprite = holder;
  }

  @on("render")
  onRender(dt: number) {
    // The piece the hand is on lights up on its holder, so the glow hugs
    // the sprite's real silhouette and rides every spring with it.
    const handId =
      this.game.entities.tryGetSingleton(BenchArrangeView)?.handPieceId() ??
      null;
    this.motion.setLit(this.pieceId === handId);
    this.motion.step(dt);
  }

  @on("destroy")
  onDestroy() {
    // The engine takes the holder (the sprite) down; this reaches the
    // rim ghost, which lives in the dive's rim layer instead.
    this.motion.destroy();
  }
}
